import { useEffect, useRef } from "react";

// High-performance animated starfield.
// Perf strategy (tuned for software-rendered / GPU-less environments too):
//  - Canvas runs at DPR 1 (stars don't need retina) -> far fewer pixels.
//  - Static nebula glows live in a CSS layer (see render), so the canvas
//    never blits a full-screen image per frame.
//  - NO per-frame gradient / shadowBlur creation:
//      * glowing stars + shooting-star heads use pre-rendered glow sprites
//      * shooting-star trails use ONE pre-rendered gradient sprite drawn
//        with translate/rotate/drawImage
//  - Tiny stars drawn with fillRect (cheaper than arc), capped count,
//    loop paused when tab hidden. Honors prefers-reduced-motion.
export const Starfield = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const starsRef = useRef([]);
  const shootersRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const nextShootRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let width = 0;
    let height = 0;
    let running = true;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const palette = ["#ffffff", "#dfe8ff", "#bfd3ff", "#a9c3ff"];
    const rand = (min, max) => Math.random() * (max - min) + min;

    // --- Pre-rendered glow sprite (soft radial + faint cross) --------------
    const makeGlowSprite = (rgb) => {
      const size = 40;
      const c = document.createElement("canvas");
      c.width = size;
      c.height = size;
      const g = c.getContext("2d");
      const grd = g.createRadialGradient(
        size / 2, size / 2, 0, size / 2, size / 2, size / 2
      );
      grd.addColorStop(0, `rgba(${rgb},1)`);
      grd.addColorStop(0.2, `rgba(${rgb},0.55)`);
      grd.addColorStop(1, `rgba(${rgb},0)`);
      g.fillStyle = grd;
      g.fillRect(0, 0, size, size);
      return c;
    };
    const glowWhite = makeGlowSprite("255,255,255");
    const glowBlue = makeGlowSprite("150,185,255");

    // --- Pre-rendered shooting-star trail sprite (bright head on right) ----
    const trailSprite = (() => {
      const w = 256;
      const h = 8;
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const g = c.getContext("2d");
      const grd = g.createLinearGradient(0, 0, w, 0);
      grd.addColorStop(0, "rgba(120,170,255,0)");
      grd.addColorStop(0.65, "rgba(150,185,255,0.5)");
      grd.addColorStop(1, "rgba(255,255,255,1)");
      g.fillStyle = grd;
      // tapered trail (thicker near head)
      g.beginPath();
      g.moveTo(0, h / 2);
      g.lineTo(w, 0);
      g.lineTo(w, h);
      g.closePath();
      g.fill();
      return c;
    })();

    const makeStars = () => {
      const count = Math.min(170, Math.floor((width * height) / 12000));
      const stars = [];
      for (let i = 0; i < count; i++) {
        const bright = Math.random() > 0.9;
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r: bright ? rand(1.1, 1.8) : rand(0.6, 1.3),
          base: rand(0.25, 0.85),
          amp: rand(0.12, 0.4),
          phase: Math.random() * Math.PI * 2,
          speed: rand(0.6, 1.8),
          depth: rand(0.2, 1),
          color: palette[Math.floor(Math.random() * palette.length)],
          glow: bright,
          sprite: Math.random() > 0.5 ? glowBlue : glowWhite,
        });
      }
      starsRef.current = stars;
    };

    const spawnShooter = () => {
      const startX = rand(-0.1, 0.7) * width;
      const startY = rand(-0.05, 0.4) * height;
      const angle = rand(Math.PI * 0.12, Math.PI * 0.28);
      const speed = rand(9, 15);
      shootersRef.current.push({
        x: startX,
        y: startY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        angle,
        len: rand(120, 230),
        life: 0,
        ttl: rand(60, 90),
        width: rand(5, 9),
      });
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      // DPR fixed at 1 for performance.
      canvas.width = Math.floor(width);
      canvas.height = Math.floor(height);
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      makeStars();
    };

    const drawShooter = (sh) => {
      const t = sh.life / sh.ttl;
      const fade = Math.sin(Math.min(t, 1) * Math.PI);
      ctx.save();
      ctx.translate(sh.x, sh.y);
      ctx.rotate(sh.angle);
      ctx.globalAlpha = fade;
      // trail sprite: bright end (right) sits at the head (origin)
      ctx.drawImage(trailSprite, -sh.len, -sh.width / 2, sh.len, sh.width);
      ctx.restore();
      // bright head glow
      const hs = 20;
      ctx.globalAlpha = fade;
      ctx.drawImage(glowWhite, sh.x - hs / 2, sh.y - hs / 2, hs, hs);
      ctx.globalAlpha = 1;
    };

    let frame = 0;
    let lastTs = 0;
    const minDelta = 1000 / 40; // cap rendering to ~40fps to save CPU

    const loop = (ts) => {
      if (!running) return;
      rafRef.current = requestAnimationFrame(loop);
      if (ts - lastTs < minDelta) return; // throttle
      lastTs = ts;
      draw();
    };

    const draw = () => {
      frame++;
      ctx.clearRect(0, 0, width, height);

      const mx = (mouseRef.current.x - width / 2) * 0.012;
      const my = (mouseRef.current.y - height / 2) * 0.012;

      for (const s of starsRef.current) {
        s.phase += 0.02 * s.speed;
        const alpha = Math.max(
          0.05,
          Math.min(1, s.base + Math.sin(s.phase) * s.amp)
        );
        const px = s.x + mx * s.depth * 8;
        const py = s.y + my * s.depth * 8;
        ctx.globalAlpha = alpha;
        if (s.glow) {
          const d = s.r * 9;
          ctx.drawImage(s.sprite, px - d / 2, py - d / 2, d, d);
        } else {
          ctx.fillStyle = s.color;
          const sz = s.r * 1.6;
          ctx.fillRect(px, py, sz, sz);
        }
      }
      ctx.globalAlpha = 1;

      if (frame >= nextShootRef.current) {
        spawnShooter();
        nextShootRef.current = frame + Math.floor(rand(150, 380));
      }
      if (shootersRef.current.length) {
        const alive = [];
        for (const sh of shootersRef.current) {
          sh.x += sh.vx;
          sh.y += sh.vy;
          sh.life++;
          drawShooter(sh);
          if (sh.life < sh.ttl && sh.x < width + 300 && sh.y < height + 300) {
            alive.push(sh);
          }
        }
        shootersRef.current = alive;
      }
    };

    const drawStatic = () => {
      ctx.clearRect(0, 0, width, height);
      for (const s of starsRef.current) {
        ctx.globalAlpha = s.base;
        if (s.glow) {
          const d = s.r * 9;
          ctx.drawImage(s.sprite, s.x - d / 2, s.y - d / 2, d, d);
        } else {
          ctx.fillStyle = s.color;
          const sz = s.r * 1.6;
          ctx.fillRect(s.x, s.y, sz, sz);
        }
      }
      ctx.globalAlpha = 1;
    };

    const onMouse = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      } else if (!prefersReduced && !running) {
        running = true;
        lastTs = 0;
        rafRef.current = requestAnimationFrame(loop);
      }
    };

    resize();
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVisibility);
    if (!prefersReduced) {
      window.addEventListener("mousemove", onMouse, { passive: true });
      nextShootRef.current = 90;
      rafRef.current = requestAnimationFrame(loop);
    } else {
      drawStatic();
    }

    return () => {
      running = false;
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouse);
      document.removeEventListener("visibilitychange", onVisibility);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <>
      {/* Static nebula glows live in CSS so the canvas never blits a
          full-screen image per frame. */}
      <div
        aria-hidden="true"
        className="fixed inset-0 -z-20 pointer-events-none"
        style={{
          background:
            "radial-gradient(40vw 40vw at 18% 25%, rgba(58,123,255,0.12), transparent 60%)," +
            "radial-gradient(34vw 34vw at 82% 18%, rgba(91,140,255,0.10), transparent 60%)," +
            "radial-gradient(42vw 42vw at 60% 88%, rgba(27,63,160,0.12), transparent 60%)",
        }}
      />
      <canvas
        ref={canvasRef}
        data-testid="starfield-canvas"
        aria-hidden="true"
        className="fixed inset-0 -z-10 pointer-events-none"
      />
    </>
  );
};
